import { useSearchParams } from 'react-router-dom';
/*
 * LOGIN TROUBLESHOOTING GUIDE
 * ==========================
 * 
 * Common login issues and their solutions:
 * 
 * 1. CONNECTION REFUSED ERRORS (ERR_CONNECTION_REFUSED)
 *    Problem: Frontend trying to connect to wrong API port
 *    Symptoms: "Failed to fetch", "net::ERR_CONNECTION_REFUSED"
 *    Solution: Check ui/.env.local file:
 *      - NEXT_PUBLIC_API_URL should match the actual API server port
 *      - Default: http://localhost:9000 (NOT 8000, 8003, or 3000)
 *      - Verify API server is running: curl http://localhost:9000/health
 * 
 * 2. CORS ERRORS
 *    Problem: Cross-origin request blocked
 *    Symptoms: "CORS policy", "blocked by CORS"
 *    Solution: Check apip/src/server.py CORS configuration:
 *      - Ensure frontend URL (http://localhost:3000) is in allow_origins
 *      - Check allow_credentials: True
 *      - Verify allow_methods includes POST, GET, OPTIONS
 * 
 * 3. 404 API ENDPOINT NOT FOUND
 *    Problem: API route not registered or wrong path
 *    Symptoms: "Route not found", 404 status
 *    Solution: Check apip/src/server.py:
 *      - Verify auth.router is included with correct prefix (/auth)
 *      - Check route definition in apip/src/routes/auth.py
 * 
 * 4. GOOGLE LOGIN FAILS
 *    Problem: Google OAuth configuration issues
 *    Symptoms: Google sign-in button not appearing or failing
 *    Solution: Check ui/.env.local:
 *      - NEXT_PUBLIC_GOOGLE_CLIENT_ID must be valid
 *      - Google Console: Add http://localhost:3000 to authorized origins
 * 
 * 5. API KEY ISSUES
 *    Problem: Invalid or missing API key
 *    Symptoms: 401 Unauthorized, "Invalid API key"
 *    Solution: Check ui/.env.local and api.ts:
 *      - NEXT_PUBLIC_API_KEY should match backend expectation
 *      - Default: "dev-api-key-12345"
 * 
 * 6. SERVER NOT RUNNING
 *    Problem: Backend API server is down
 *    Symptoms: Connection refused, timeout
 *    Solution: Start the server:
 *      - Run: ./start.sh (starts both frontend and backend)
 *      - Or: cd apip && python src/server.py
 *      - Check: ps aux | grep uvicorn
 * 
 * QUICK DEBUG CHECKLIST:
 * - [ ] Backend server running on port 9000? (curl http://localhost:9000/health)
 * - [ ] Frontend on port 3000? (check browser)
 * - [ ] NEXT_PUBLIC_API_URL=http://localhost:9000 in ui/.env.local?
 * - [ ] Valid GOOGLE_CLIENT_ID in ui/.env.local?
 * - [ ] No proxy/firewall blocking requests?
 * - [ ] Check browser Network tab for actual request details
 * - [ ] Check browser Console for detailed error messages
 */

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Mail, Lock, LogIn, Eye, EyeOff, Send, CheckCircle, ArrowLeft } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { apiRequest, GOOGLE_CLIENT_ID } from '@/config/api'

interface SignInFormProps {
  onSwitch: () => void
}

type LoginMode = 'credentials' | 'otp_request' | 'otp_verify'

export default function SignInForm({ onSwitch }: SignInFormProps) {
  const [searchParams] = useSearchParams()
  const returnUrl = searchParams.get('returnUrl') || '/recruiter/dashboard'
  
  const [loginMode, setLoginMode] = useState<LoginMode>('credentials')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    console.log('🔧 SignInForm useEffect - GOOGLE_CLIENT_ID from config:', GOOGLE_CLIENT_ID)
    
    // Load Google Identity Services script
    if (!document.getElementById('google-identity-script')) {
      const script = document.createElement('script')
      script.id = 'google-identity-script'
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.defer = true
      document.head.appendChild(script)
      
      script.onload = () => {
        console.log('🔧 Google Identity Services script loaded')
        
        // Initialize Google Identity Services
        if (window.google && window.google.accounts) {
          const actualClientId = GOOGLE_CLIENT_ID;
          
          if (actualClientId) {
            try {
              (window.google.accounts.id as any).initialize({
                client_id: actualClientId,
                callback: handleGoogleCredentialResponse,
                auto_select: false,
                cancel_on_tap_outside: true,
                ux_mode: 'popup',
                use_fedcm_for_prompt: false
              })
              
              console.log('✅ Google OAuth initialized with client_id:', actualClientId)
            } catch (error) {
              console.error('❌ Google OAuth initialization failed:', error)
            }
          } else {
            console.error('❌ Google Client ID not configured')
          }
        } else {
          console.error('❌ Google Identity Services not available')
        }
      }
      
      script.onerror = (error) => {
        console.error('❌ Failed to load Google Identity Services script:', error)
      }
    } else {
      console.log('🔧 Google Identity Services script already exists')
    }
  }, [])

  const handleGoogleCredentialResponse = async (response: any) => {
    setError('')

    try {
      const result = await apiClient.googleLogin(response.credential)
      
      // Handle both direct token response and nested data response
      const token = (result as any).token || result.data?.token
      const user = (result as any).user || result.data?.user
      
      if (result.success && token) {
        localStorage.setItem('token', token)
        localStorage.setItem('currentUser', JSON.stringify(user))
        
        console.log('✅ Google login successful - token stored:', token.substring(0, 20) + '...')
        console.log('✅ Google user data stored:', user)
        
        window.location.href = returnUrl
      } else {
        const errorMessage = typeof (result as any).error === 'string'
          ? (result as any).error
          : (result as any).error?.message || 'Google login failed'
        setError(errorMessage)
      }
    } catch (error) {
      console.error('Google login failed:', error)
      setError('Google login failed. Please try again.')
    }
  }

  const handleGoogleSignIn = () => {
    if (window.google) {
      window.google.accounts.id.prompt()
    }
  }

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const response = await apiClient.login(email, password)
      
      // Handle both direct token response and nested data response
      const token = (response as any).token || response.data?.token
      const user = (response as any).user || response.data?.user
      
      if (response.success && token) {
        // Store token and user info
        localStorage.setItem('token', token)
        localStorage.setItem('currentUser', JSON.stringify(user))
        
        console.log('✅ Login successful - token stored:', token.substring(0, 20) + '...')
        console.log('✅ User data stored:', user)
        
        // Redirect to return URL or default to recruiter dashboard
        window.location.href = returnUrl
      } else {
        // Handle different error formats from the API
        const errorMessage = typeof (response as any).error === 'string'
          ? (response as any).error
          : (response as any).error?.message || 
            (response.data as any)?.error?.message || 
            'Invalid email or password'
        setError(errorMessage)
      }
    } catch (error) {
      console.error('Login failed:', error)
      setError('Login failed. Please check your credentials and try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleSendOTP = async () => {
    if (!email) {
      setError('Please enter your email address')
      return
    }

    setLoading(true)
    setError('')

    try {
      const response = await apiRequest('auth/send-otp', {
        method: 'POST',
        body: JSON.stringify({ email })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setLoginMode('otp_verify')
      } else {
        setError(data.detail || 'Failed to send OTP. Please try again.')
      }
    } catch (error) {
      console.error('Send OTP failed:', error)
      setError('Failed to send OTP. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const response = await apiRequest('auth/verify-otp', {
        method: 'POST',
        body: JSON.stringify({ email, otp_code: otpCode })
      })

      const data = await response.json()

      if (response.ok && data.success && data.token) {
        // Store token and user info
        localStorage.setItem('token', data.token)
        localStorage.setItem('currentUser', JSON.stringify(data.user))
        
        console.log('✅ OTP Login successful - token stored:', data.token.substring(0, 20) + '...')
        console.log('✅ User data stored:', data.user)
        
        // Redirect to return URL or default to recruiter dashboard
        window.location.href = returnUrl
      } else {
        setError(data.detail || 'Invalid OTP code. Please try again.')
      }
    } catch (error) {
      console.error('Verify OTP failed:', error)
      setError('OTP verification failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleBackToCredentials = () => {
    setLoginMode('credentials')
    setOtpCode('')
    setError('')
  }

  return (
    <motion.div
      initial={{ x: -20, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -20, opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="w-full"
    >
      <div className="mb-6 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {loginMode === 'credentials' ? 'Sign In' : 
           loginMode === 'otp_request' ? 'Login with OTP' : 
           'Enter Verification Code'}
        </h2>
        <p className="text-gray-700">
          {loginMode === 'credentials' ? 'Welcome back! Please sign in to your account' : 
           loginMode === 'otp_request' ? 'We\'ll send you a verification code' :
           `We've sent a 6-digit code to ${email}`}
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Credentials Login Form */}
      {loginMode === 'credentials' && (
        <form onSubmit={handleSignIn} className="space-y-4">
          {/* Email Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* Password Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Remember Me & Forgot Password */}
          <div className="flex items-center justify-between">
            <label className="flex items-center">
              <input type="checkbox" className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
              <span className="ml-2 text-sm text-gray-700">Remember me</span>
            </label>
            <button
              type="button"
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Forgot password?
            </button>
          </div>

          {/* Sign In Button */}
          <button
            type="submit"
            disabled={loading || !email || !password}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center ${
              loading || !email || !password
                ? 'bg-gray-400 cursor-not-allowed text-white'
                : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white'
            }`}
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <LogIn className="w-5 h-5 mr-2" />
            )}
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      )}

      {/* OTP Request Form */}
      {loginMode === 'otp_request' && (
        <div className="space-y-4">
          {/* Email Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* Send OTP Button */}
          <button
            onClick={handleSendOTP}
            disabled={loading || !email}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center ${
              loading || !email
                ? 'bg-gray-400 cursor-not-allowed text-white'
                : 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white'
            }`}
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <Send className="w-5 h-5 mr-2" />
            )}
            {loading ? 'Sending OTP...' : 'Send Verification Code'}
          </button>

          {/* Back to Credentials */}
          <button
            onClick={handleBackToCredentials}
            className="w-full py-2 px-4 text-gray-600 hover:text-gray-800 font-medium transition-colors flex items-center justify-center"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Password Login
          </button>
        </div>
      )}

      {/* OTP Verification Form */}
      {loginMode === 'otp_verify' && (
        <form onSubmit={handleVerifyOTP} className="space-y-4">
          {/* OTP Code Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Verification Code
            </label>
            <input
              type="text"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Enter 6-digit code"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-lg tracking-widest"
              maxLength={6}
              style={{ letterSpacing: '0.5em' }}
            />
            <p className="text-xs text-gray-500 mt-1">
              Check your email for the verification code
            </p>
          </div>

          {/* Verify Button */}
          <button
            type="submit"
            disabled={loading || otpCode.length !== 6}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center ${
              loading || otpCode.length !== 6
                ? 'bg-gray-400 cursor-not-allowed text-white'
                : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white'
            }`}
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <CheckCircle className="w-5 h-5 mr-2" />
            )}
            {loading ? 'Verifying...' : 'Verify & Sign In'}
          </button>

          {/* Back to Credentials */}
          <button
            onClick={handleBackToCredentials}
            type="button"
            className="w-full py-2 px-4 text-gray-600 hover:text-gray-800 font-medium transition-colors flex items-center justify-center"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Password Login
          </button>
        </form>
      )}

      {/* Show divider and options only for credentials mode */}
      {loginMode === 'credentials' && (
        <>
          {/* OR Divider */}
          <div className="my-6 flex items-center">
            <div className="flex-1 border-t border-gray-300"></div>
            <span className="px-4 text-sm text-gray-500">OR</span>
            <div className="flex-1 border-t border-gray-300"></div>
          </div>

          {/* Alternative Login Options */}
          <div className="space-y-3">
            {/* Google Sign In Button */}
            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center border ${
                loading
                  ? 'bg-gray-100 border-gray-300 cursor-not-allowed text-gray-500'
                  : 'bg-white border-gray-300 hover:bg-gray-50 text-gray-700 hover:border-gray-400'
              }`}
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-3" />
              ) : (
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              )}
              {loading ? 'Signing in with Google...' : 'Continue with Google'}
            </button>

            {/* OTP Login Button */}
            <button
              onClick={() => setLoginMode('otp_request')}
              disabled={loading}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center border ${
                loading
                  ? 'bg-gray-100 border-gray-300 cursor-not-allowed text-gray-500'
                  : 'bg-white border-green-300 hover:bg-green-50 text-green-700 hover:border-green-400'
              }`}
            >
              <Mail className="w-5 h-5 mr-3" />
              Login with Email OTP
            </button>
          </div>
        </>
      )}


      {/* Switch to Register */}
      <div className="mt-6 pt-6 border-t border-gray-200 text-center">
        <p className="text-sm text-gray-700">
          Don't have an account?{' '}
          <button
            onClick={onSwitch}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Create one here
          </button>
        </p>
      </div>
    </motion.div>
  )
}
